/**
 * Qui peut saisir quoi dans le panneau d'arbitrage, selon le statut du dossier
 * et le rôle de l'utilisateur.
 *
 * Deux colonnes de saisie coexistent : « Arbitre » (le chiffrage de premier
 * niveau) et « Superviseur » (la relecture). Un `arbitrator` ne remplit que la
 * première et passe obligatoirement par la supervision ; un `god` cumule les
 * deux casquettes et peut donc aussi soumettre directement — c'est ce que
 * traite déjà `determineStatus` côté backend.
 *
 * `previous` porte l'état courant : les statuts hors circuit de saisie
 * (`refused`, `accepted`…) le laissent inchangé, comme avant.
 */
export type ArbitrationGating = {
  arbitratorInputsDisabled: boolean;
  supervisorInputsDisabled: boolean;
};

export const computeArbitrationGating = (
  status: string | undefined,
  role: string | undefined,
  previous: ArbitrationGating,
): ArbitrationGating => {
  // Dossier clos : plus personne ne touche à rien.
  if (status === 'processed') {
    return { arbitratorInputsDisabled: true, supervisorInputsDisabled: true };
  }

  const enCoursDeSaisie = status === 'to_be_processed' || status === undefined || status === 'awaiting_supervision';
  if (!enCoursDeSaisie) return previous;

  // Un god arbitre ET supervise. Sans ça il ne pouvait que superviser, et le
  // chemin « god qui soumet directement » restait inatteignable depuis l'UI.
  if (role === 'god') {
    return { arbitratorInputsDisabled: false, supervisorInputsDisabled: false };
  }

  if (role === 'arbitrator') {
    return { arbitratorInputsDisabled: false, supervisorInputsDisabled: true };
  }

  return {
    arbitratorInputsDisabled: true,
    // Sur `awaiting_supervision`, la colonne superviseur gardait son état.
    supervisorInputsDisabled: status === 'awaiting_supervision' ? previous.supervisorInputsDisabled : false,
  };
};
